import { useState, useEffect, useCallback } from "react";
import { useAuth, useSystem, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  Palette, 
  Monitor, 
  Type, 
  Image, 
  Save, 
  RotateCcw, 
  Loader2,
  Eye,
  LogIn,
  LayoutDashboard,
  Building2,
  CheckCircle2,
  Phone,
  Mail,
  Globe,
  MapPin,
  Clock,
  FileText,
  Plus,
  Trash2,
  Facebook,
  Instagram,
  Linkedin,
  Twitter
} from "lucide-react";

export default function PersonalizacionPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { getAuthHeaders } = useAuth();
  const { refetchSystemInfo } = useSystem();
  
  const [settings, setSettings] = useState({
    // Login page
    login_title: "Software de Gestión Empresarial",
    login_title_highlight: "",
    login_subtitle: "Todo gestionalo desde un solo lugar.",
    login_copyright: "© 2026 QAsesores. Corredores de Seguros y Reaseguros SRL.",
    login_logo_url: "",
    login_show_features: true,
    login_feature1_text: "PDF & DOC",
    login_feature2_text: "Gestión Total",
    login_background_color: "#E8E6E6",
    // Dashboard
    dashboard_welcome_text: "Bienvenido",
    dashboard_subtitle: "Panel de Control",
    // Company / Branding
    company_name: "QAsesores",
    company_logo_url: "",
    primary_color: "#0C314C",
    accent_color: "#45E69A",
    show_emergent_badge: false,
    // System Data
    system_name: "QAsesores",
    system_slogan: "Corredores de Seguros y Reaseguros",
    system_address: "",
    system_city: "",
    system_country: "República Dominicana",
    system_phone: "",
    system_phone_secondary: "",
    system_email: "",
    system_email_secondary: "",
    system_rnc: "",
    system_website: "",
    system_business_hours: "Lunes a Viernes 8:00 AM - 5:00 PM",
    system_timezone: "America/Santo_Domingo",
    // Social Media
    social_facebook: "",
    social_instagram: "",
    social_twitter: "",
    social_linkedin: "",
    social_whatsapp: "",
    // Custom Fields
    custom_fields: []
  });

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/config/personalization`, getAuthHeaders());
      setSettings(prev => ({ ...prev, ...res.data }));
    } catch (err) {
      console.error("Error loading personalization:", err);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/config/personalization`, settings, getAuthHeaders());
      toast.success("Personalización guardada correctamente");
      // Refresh system info to update the app title and header
      refetchSystemInfo();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("¿Restablecer todos los valores a los predeterminados?")) return;
    
    try {
      await axios.post(`${API}/config/personalization/reset`, {}, getAuthHeaders());
      toast.success("Valores restablecidos");
      fetchSettings();
      // Refresh system info to update the app title and header
      refetchSystemInfo();
    } catch (err) {
      toast.error("Error al restablecer");
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const addCustomField = () => {
    setSettings(prev => ({
      ...prev,
      custom_fields: [...(prev.custom_fields || []), { label: "", value: "" }]
    }));
  };

  const updateCustomField = (index, field, value) => {
    setSettings(prev => {
      const newFields = [...(prev.custom_fields || [])];
      newFields[index] = { ...newFields[index], [field]: value };
      return { ...prev, custom_fields: newFields };
    });
  };

  const removeCustomField = (index) => {
    setSettings(prev => ({
      ...prev,
      custom_fields: (prev.custom_fields || []).filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex bg-slate-50 dark:bg-slate-900">
        <Sidebar />
        <main className="ml-0 md:ml-64 flex-1 p-8 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#0C314C]" />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="ml-0 md:ml-64 p-4 md:p-8 overflow-auto" data-testid="personalizacion-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#0C314C] dark:text-white font-['Manrope']">
              Personalización
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Personaliza los textos e imágenes de las pantallas del sistema
            </p>
          </div>
          <div className="flex gap-3">
            <Button 
              onClick={handleReset}
              variant="outline"
              className="border-slate-300"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Restablecer
            </Button>
            <Button 
              onClick={handleSave}
              disabled={saving}
              className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
              data-testid="save-personalization-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Guardar Cambios
            </Button>
          </div>
        </div>

        <Tabs defaultValue="system" className="space-y-6">
          <TabsList className="grid w-full max-w-3xl grid-cols-4">
            <TabsTrigger value="system" className="flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Datos del Sistema
            </TabsTrigger>
            <TabsTrigger value="login" className="flex items-center gap-2">
              <LogIn className="w-4 h-4" />
              Página de Login
            </TabsTrigger>
            <TabsTrigger value="dashboard" className="flex items-center gap-2">
              <LayoutDashboard className="w-4 h-4" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="branding" className="flex items-center gap-2">
              <Palette className="w-4 h-4" />
              Marca / Colores
            </TabsTrigger>
          </TabsList>

          {/* System Data Tab */}
          <TabsContent value="system">
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              {/* Basic Info Card */}
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center gap-2 dark:text-white text-lg">
                    <Building2 className="w-5 h-5 text-[#0C314C]" />
                    Información Básica
                  </CardTitle>
                  <CardDescription className="dark:text-slate-400 text-sm">
                    Datos principales del sistema y la empresa
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="dark:text-slate-300">Nombre del Sistema</Label>
                      <Input
                        value={settings.system_name}
                        onChange={(e) => updateSetting("system_name", e.target.value)}
                        placeholder="Mi Sistema"
                        className="dark:bg-slate-700 dark:border-slate-600"
                        data-testid="system-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="dark:text-slate-300">Slogan / Descripción</Label>
                      <Input
                        value={settings.system_slogan}
                        onChange={(e) => updateSetting("system_slogan", e.target.value)}
                        placeholder="Tu slogan aquí"
                        className="dark:bg-slate-700 dark:border-slate-600"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="dark:text-slate-300">RNC / Identificación Fiscal</Label>
                    <Input
                      value={settings.system_rnc}
                      onChange={(e) => updateSetting("system_rnc", e.target.value)}
                      placeholder="123-45678-9"
                      className="dark:bg-slate-700 dark:border-slate-600"
                      data-testid="system-rnc-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="dark:text-slate-300">Dirección</Label>
                    <Input
                      value={settings.system_address}
                      onChange={(e) => updateSetting("system_address", e.target.value)}
                      placeholder="Calle Principal #123"
                      className="dark:bg-slate-700 dark:border-slate-600"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="dark:text-slate-300">Ciudad</Label>
                      <Input
                        value={settings.system_city}
                        onChange={(e) => updateSetting("system_city", e.target.value)}
                        placeholder="Santo Domingo"
                        className="dark:bg-slate-700 dark:border-slate-600"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="dark:text-slate-300">País</Label>
                      <Input
                        value={settings.system_country}
                        onChange={(e) => updateSetting("system_country", e.target.value)}
                        placeholder="República Dominicana"
                        className="dark:bg-slate-700 dark:border-slate-600"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Contact Info Card */}
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center gap-2 dark:text-white text-lg">
                    <Phone className="w-5 h-5 text-[#0C314C]" />
                    Contacto
                  </CardTitle>
                  <CardDescription className="dark:text-slate-400 text-sm">
                    Teléfonos, emails y sitio web
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="dark:text-slate-300 flex items-center gap-2">
                        <Phone className="w-4 h-4" />
                        Teléfono Principal
                      </Label>
                      <Input
                        value={settings.system_phone}
                        onChange={(e) => updateSetting("system_phone", e.target.value)}
                        placeholder="809-555-1234"
                        className="dark:bg-slate-700 dark:border-slate-600"
                        data-testid="system-phone-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="dark:text-slate-300">Teléfono Secundario</Label>
                      <Input
                        value={settings.system_phone_secondary}
                        onChange={(e) => updateSetting("system_phone_secondary", e.target.value)}
                        placeholder="809-555-5678"
                        className="dark:bg-slate-700 dark:border-slate-600"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="dark:text-slate-300 flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        Email Principal
                      </Label>
                      <Input
                        value={settings.system_email}
                        onChange={(e) => updateSetting("system_email", e.target.value)}
                        placeholder="info@miempresa.com"
                        className="dark:bg-slate-700 dark:border-slate-600"
                        data-testid="system-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="dark:text-slate-300">Email Secundario</Label>
                      <Input
                        value={settings.system_email_secondary}
                        onChange={(e) => updateSetting("system_email_secondary", e.target.value)}
                        placeholder="soporte@miempresa.com"
                        className="dark:bg-slate-700 dark:border-slate-600"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="dark:text-slate-300 flex items-center gap-2">
                      <Globe className="w-4 h-4" />
                      Sitio Web
                    </Label>
                    <Input
                      value={settings.system_website}
                      onChange={(e) => updateSetting("system_website", e.target.value)}
                      placeholder="https://www.miempresa.com"
                      className="dark:bg-slate-700 dark:border-slate-600"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="dark:text-slate-300 flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Horario de Atención
                    </Label>
                    <Input
                      value={settings.system_business_hours}
                      onChange={(e) => updateSetting("system_business_hours", e.target.value)}
                      placeholder="Lunes a Viernes 8:00 AM - 5:00 PM"
                      className="dark:bg-slate-700 dark:border-slate-600"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Social Media Card */}
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center gap-2 dark:text-white text-lg">
                    <Globe className="w-5 h-5 text-[#0C314C]" />
                    Redes Sociales
                  </CardTitle>
                  <CardDescription className="dark:text-slate-400 text-sm">
                    Enlaces a perfiles de redes sociales
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="dark:text-slate-300 flex items-center gap-2">
                        <Facebook className="w-4 h-4 text-blue-600" />
                        Facebook
                      </Label>
                      <Input
                        value={settings.social_facebook}
                        onChange={(e) => updateSetting("social_facebook", e.target.value)}
                        placeholder="https://facebook.com/miempresa"
                        className="dark:bg-slate-700 dark:border-slate-600"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="dark:text-slate-300 flex items-center gap-2">
                        <Instagram className="w-4 h-4 text-pink-600" />
                        Instagram
                      </Label>
                      <Input
                        value={settings.social_instagram}
                        onChange={(e) => updateSetting("social_instagram", e.target.value)}
                        placeholder="https://instagram.com/miempresa"
                        className="dark:bg-slate-700 dark:border-slate-600"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="dark:text-slate-300 flex items-center gap-2">
                        <Twitter className="w-4 h-4 text-sky-500" />
                        Twitter / X
                      </Label>
                      <Input
                        value={settings.social_twitter}
                        onChange={(e) => updateSetting("social_twitter", e.target.value)}
                        placeholder="https://twitter.com/miempresa"
                        className="dark:bg-slate-700 dark:border-slate-600"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="dark:text-slate-300 flex items-center gap-2">
                        <Linkedin className="w-4 h-4 text-blue-700" />
                        LinkedIn
                      </Label>
                      <Input
                        value={settings.social_linkedin}
                        onChange={(e) => updateSetting("social_linkedin", e.target.value)}
                        placeholder="https://linkedin.com/company/miempresa"
                        className="dark:bg-slate-700 dark:border-slate-600"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="dark:text-slate-300 flex items-center gap-2">
                      <Phone className="w-4 h-4 text-green-600" />
                      WhatsApp Business
                    </Label>
                    <Input
                      value={settings.social_whatsapp}
                      onChange={(e) => updateSetting("social_whatsapp", e.target.value)}
                      placeholder="https://wa.me/18095551234"
                      className="dark:bg-slate-700 dark:border-slate-600"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Custom Fields Card */}
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center gap-2 dark:text-white text-lg">
                    <FileText className="w-5 h-5 text-[#0C314C]" />
                    Campos Personalizados
                  </CardTitle>
                  <CardDescription className="dark:text-slate-400 text-sm">
                    Agrega información adicional del sistema
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {(settings.custom_fields || []).map((field, index) => (
                    <div key={index} className="flex gap-3 items-start">
                      <div className="flex-1 grid grid-cols-2 gap-3">
                        <Input
                          value={field.label}
                          onChange={(e) => updateCustomField(index, "label", e.target.value)}
                          placeholder="Nombre del campo"
                          className="dark:bg-slate-700 dark:border-slate-600"
                        />
                        <Input
                          value={field.value}
                          onChange={(e) => updateCustomField(index, "value", e.target.value)}
                          placeholder="Valor"
                          className="dark:bg-slate-700 dark:border-slate-600"
                        />
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeCustomField(index)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  
                  <Button
                    variant="outline"
                    onClick={addCustomField}
                    className="w-full border-dashed dark:border-slate-600"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Agregar Campo Personalizado
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Login Page Tab */}
          <TabsContent value="login">
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              {/* Settings Card */}
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center gap-2 dark:text-white text-lg">
                    <Type className="w-5 h-5 text-[#0C314C]" />
                    Textos de la Página de Login
                  </CardTitle>
                  <CardDescription className="dark:text-slate-400 text-sm">
                    Personaliza los textos que aparecen en la pantalla de inicio de sesión
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3 max-h-[calc(100vh-320px)] overflow-y-auto">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="dark:text-slate-300 text-sm">Título Principal</Label>
                      <Input
                        value={settings.login_title}
                        onChange={(e) => updateSetting("login_title", e.target.value)}
                        placeholder="Software de Gestión Empresarial"
                        className="dark:bg-slate-700 dark:border-slate-600"
                        data-testid="login-title-input"
                      />
                    </div>

                    <div className="space-y-1">
                      <Label className="dark:text-slate-300 text-sm">Texto Resaltado (opcional)</Label>
                      <Input
                        value={settings.login_title_highlight}
                        onChange={(e) => updateSetting("login_title_highlight", e.target.value)}
                        placeholder="Ej: inteligencia artificial"
                        className="dark:bg-slate-700 dark:border-slate-600"
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <Label className="dark:text-slate-300 text-sm">Subtítulo / Descripción</Label>
                    <Textarea
                      value={settings.login_subtitle}
                      onChange={(e) => updateSetting("login_subtitle", e.target.value)}
                      placeholder="Todo gestionalo desde un solo lugar."
                      className="dark:bg-slate-700 dark:border-slate-600 min-h-[60px]"
                      data-testid="login-subtitle-input"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="dark:text-slate-300 text-sm">Texto de Copyright</Label>
                      <Input
                        value={settings.login_copyright}
                        onChange={(e) => updateSetting("login_copyright", e.target.value)}
                        placeholder="© 2026 Mi Empresa"
                        className="dark:bg-slate-700 dark:border-slate-600"
                        data-testid="login-copyright-input"
                      />
                    </div>

                    <div className="space-y-1">
                      <Label className="dark:text-slate-300 text-sm">URL del Logo</Label>
                      <Input
                        value={settings.login_logo_url}
                        onChange={(e) => updateSetting("login_logo_url", e.target.value)}
                        placeholder="https://..."
                        className="dark:bg-slate-700 dark:border-slate-600"
                      />
                    </div>
                  </div>

                  <div className="pt-3 border-t dark:border-slate-700 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="dark:text-slate-300 text-sm">Mostrar características</Label>
                        <p className="text-xs text-slate-500">PDF & DOC, Análisis IA, etc.</p>
                      </div>
                      <Switch
                        checked={settings.login_show_features}
                        onCheckedChange={(checked) => updateSetting("login_show_features", checked)}
                      />
                    </div>

                    {settings.login_show_features && (
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-sm dark:text-slate-300">Característica 1</Label>
                          <Input
                            value={settings.login_feature1_text}
                            onChange={(e) => updateSetting("login_feature1_text", e.target.value)}
                            placeholder="PDF & DOC"
                            className="dark:bg-slate-700 dark:border-slate-600"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-sm dark:text-slate-300">Característica 2</Label>
                          <Input
                            value={settings.login_feature2_text}
                            onChange={(e) => updateSetting("login_feature2_text", e.target.value)}
                            placeholder="Gestión Total"
                            className="dark:bg-slate-700 dark:border-slate-600"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="pt-3 border-t dark:border-slate-700">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="dark:text-slate-300 text-sm">Mostrar badge "Made with Emergent"</Label>
                        <p className="text-xs text-slate-500">Logo de Emergent en la esquina</p>
                      </div>
                      <Switch
                        checked={settings.show_emergent_badge}
                        onCheckedChange={(checked) => updateSetting("show_emergent_badge", checked)}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Preview Card */}
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 dark:text-white">
                    <Eye className="w-5 h-5 text-[#0C314C]" />
                    Vista Previa
                  </CardTitle>
                  <CardDescription className="dark:text-slate-400">
                    Así se verá la página de login
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div 
                    className="rounded-lg overflow-hidden border dark:border-slate-600"
                    style={{ backgroundColor: settings.login_background_color }}
                  >
                    <div className="p-6 space-y-4">
                      {settings.login_logo_url && (
                        <img 
                          src={settings.login_logo_url} 
                          alt="Logo" 
                          className="h-16 w-auto"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      )}
                      <h2 
                        className="text-xl font-bold font-['Manrope']"
                        style={{ color: settings.primary_color }}
                      >
                        {settings.login_title}{" "}
                        {settings.login_title_highlight && (
                          <span style={{ color: settings.accent_color }}>
                            {settings.login_title_highlight}
                          </span>
                        )}
                      </h2>
                      <p className="text-sm text-slate-700">
                        {settings.login_subtitle}
                      </p>
                      {settings.login_show_features && (
                        <div className="flex items-center gap-3 text-xs text-slate-600">
                          <span className="flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" style={{ color: settings.accent_color }} />
                            {settings.login_feature1_text}
                          </span>
                          <span className="flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" style={{ color: settings.accent_color }} />
                            {settings.login_feature2_text}
                          </span>
                        </div>
                      )}
                      <p className="text-xs text-slate-500 pt-4 border-t border-slate-300">
                        {settings.login_copyright}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard">
            <Card className="dark:bg-slate-800 dark:border-slate-700 max-w-2xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 dark:text-white">
                  <LayoutDashboard className="w-5 h-5 text-[#0C314C]" />
                  Textos del Dashboard
                </CardTitle>
                <CardDescription className="dark:text-slate-400">
                  Personaliza los textos que aparecen en el panel principal
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="dark:text-slate-300">Texto de Bienvenida</Label>
                  <Input
                    value={settings.dashboard_welcome_text}
                    onChange={(e) => updateSetting("dashboard_welcome_text", e.target.value)}
                    placeholder="Bienvenido"
                    className="dark:bg-slate-700 dark:border-slate-600"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="dark:text-slate-300">Subtítulo del Dashboard</Label>
                  <Input
                    value={settings.dashboard_subtitle}
                    onChange={(e) => updateSetting("dashboard_subtitle", e.target.value)}
                    placeholder="Panel de Control"
                    className="dark:bg-slate-700 dark:border-slate-600"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Branding Tab */}
          <TabsContent value="branding">
            <Card className="dark:bg-slate-800 dark:border-slate-700 max-w-2xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 dark:text-white">
                  <Palette className="w-5 h-5 text-[#0C314C]" />
                  Marca y Colores
                </CardTitle>
                <CardDescription className="dark:text-slate-400">
                  Personaliza los colores y la identidad visual del sistema
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="dark:text-slate-300">Nombre de la Empresa</Label>
                  <Input
                    value={settings.company_name}
                    onChange={(e) => updateSetting("company_name", e.target.value)}
                    placeholder="Mi Empresa"
                    className="dark:bg-slate-700 dark:border-slate-600"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="dark:text-slate-300">URL del Logo de la Empresa</Label>
                  <Input
                    value={settings.company_logo_url}
                    onChange={(e) => updateSetting("company_logo_url", e.target.value)}
                    placeholder="https://..."
                    className="dark:bg-slate-700 dark:border-slate-600"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="dark:text-slate-300">Color Primario</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={settings.primary_color}
                        onChange={(e) => updateSetting("primary_color", e.target.value)}
                        className="w-12 h-10 p-1 cursor-pointer"
                      />
                      <Input
                        value={settings.primary_color}
                        onChange={(e) => updateSetting("primary_color", e.target.value)}
                        placeholder="#0C314C"
                        className="dark:bg-slate-700 dark:border-slate-600 flex-1"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="dark:text-slate-300">Color de Acento</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={settings.accent_color}
                        onChange={(e) => updateSetting("accent_color", e.target.value)}
                        className="w-12 h-10 p-1 cursor-pointer"
                      />
                      <Input
                        value={settings.accent_color}
                        onChange={(e) => updateSetting("accent_color", e.target.value)}
                        placeholder="#45E69A"
                        className="dark:bg-slate-700 dark:border-slate-600 flex-1"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="dark:text-slate-300">Color de Fondo (Login)</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={settings.login_background_color}
                      onChange={(e) => updateSetting("login_background_color", e.target.value)}
                      className="w-12 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      value={settings.login_background_color}
                      onChange={(e) => updateSetting("login_background_color", e.target.value)}
                      placeholder="#E8E6E6"
                      className="dark:bg-slate-700 dark:border-slate-600 flex-1"
                    />
                  </div>
                </div>

                {/* Color Preview */}
                <div className="pt-4 border-t dark:border-slate-700">
                  <Label className="dark:text-slate-300 mb-3 block">Vista previa de colores</Label>
                  <div className="flex gap-4">
                    <div 
                      className="w-20 h-20 rounded-lg flex items-center justify-center text-white text-xs font-medium"
                      style={{ backgroundColor: settings.primary_color }}
                    >
                      Primario
                    </div>
                    <div 
                      className="w-20 h-20 rounded-lg flex items-center justify-center text-white text-xs font-medium"
                      style={{ backgroundColor: settings.accent_color }}
                    >
                      Acento
                    </div>
                    <div 
                      className="w-20 h-20 rounded-lg flex items-center justify-center text-slate-700 text-xs font-medium border"
                      style={{ backgroundColor: settings.login_background_color }}
                    >
                      Fondo
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
